// Types personalisés
import BaseTreeNode from "@/types/BaseTreeNode";
import ObjectWidget from "@/types/Dashboard/ObjectDashboard/ObjectWidget";
import SaveModeEnum from "@/enums/SaveModeEnum";
import ObjectDashboard from "@/types/Dashboard/ObjectDashboard/ObjectDashboard";
import Pin from "@/types/FilterTree/Pin";
// Machine à état 
import { ObjectDashAdminState } from "./IObjectDashAdminState";
import { ObjDashAdminEmpty } from "./ObjDashAdminEmpty";
// Axios pour WebAPI Client
import axios, { AxiosResponse } from "axios";
import { API_URL } from "@/App.settings";
// Lib vue.js
import { Vue } from 'vue-property-decorator';
import { Dictionary } from "vue-router/types/router";

/**
 * Classe de gestion des opérations sur l'adminisatration des tableaux de bords objet lorsqu'un tableau de bord est selectionné 
 */
export class ObjDashAdminSelected extends ObjectDashAdminState {

    /** Nettoye la selection */
    public Clear(): void {
        console.debug("ObjDashAdminSelected - Clear()");
        this.store.context.commit('setIsModified', false);
        /** Returne sur un état avec un dashboard vide */
        this.store.context.commit('setLoadState', new ObjDashAdminEmpty(this.store));
    }

    /** 
     * Selectionne le noeud de l'arborescence des objets types et l'inscrit dans le context
     * @param dashNode noeud de l'arborescence des objets types
     */
    public async SelectDashNode(dashNode: BaseTreeNode): Promise<void> {
        console.debug("ObjDashAdminSelected - SelectDashNode() - dashNode : ", dashNode);

        /** OPTIMISATION : Vérifie si la selection en cour est égale à celle demandé  */
        // const currentDashboard: ObjectDashboard = this.store.context.getters['GetObjectDash']; 
        // if(dashNode.DashboardRef && dashNode.DashboardRef == currentDashboard.Id)
        //     return;

        if(dashNode.DashboardRef)
        {
            /** Inscrit le noeud dans la selection courante */
            this.store.context.commit('setDashNode', dashNode);
            /** Charger les informations si il existe déjà */ 
			await this.loadDashboard(dashNode.DashboardRef);
        }else{
            /** Nettoye la selection */
            this.Clear();
            /** Selectionne le nouveau dashnode */
            await this.store.context.dispatch('SelectDashNode', dashNode);
        }
    }

    /** 
     * Créer un nouveau Dashboard vierge 
     * @param dashNode noeud de l'arborescence des objets type de référence pour la création du tableau
     * @param [profileIds=[]] Liste de profils attachés à ce tableau
     * @param [pins=[]] Liste de pin attachés à ce tableau
     * @param [widgets=[]] Liste des widgets attachés à ce tableau
     */
    public async CreateDashboard(dashnode: BaseTreeNode, profileIds: number[], pins: Pin[], widgets: ObjectWidget[]): Promise<void> {
        console.debug("ObjDashAdminSelected - CreateDashboard() - dashnode : ", dashnode);

        /** Nettoye la selection */
        this.Clear();
        /** Créer le nouveau dashboard */
        await this.store.context.dispatch('CreateDashboard', { dashNode: dashnode, profileIds: profileIds, pins: pins, widgets: widgets })
    }

    /** 
     * Supprime un tableau de bord
     * @param dashnode  noeud lié au tableau que l'on disire supprimer 
     */
    public async RemoveDashboard(dashnode: BaseTreeNode): Promise<void> {
        console.debug("ObjDashAdminSelected - RemoveDashboard() - dashnode : ", dashnode);

        /** Si pas de tableau de bord il n'y a rien à supprimer */
        if(!dashnode.DashboardRef) return;

        /** Vérifie si le tableau à supprimer est le tableau courant */
        const currentDashboard: ObjectDashboard = this.store.context.getters['GetObjectDash']; 

        if(currentDashboard.Id == dashnode.DashboardRef){
            /** Nettoye la selection */
            this.Clear();
        }

        /** Supression du tableau de bord */
        this.removeDashboard(dashnode);
    }

    /** Sauvegarde le tableau de bord sur le serveur */
    public async SaveDashboard(dashboard: ObjectDashboard): Promise<void> {
        console.debug("ObjDashAdminSelected - SaveDashboard() - dashboard : ", dashboard);

        const saveMode: SaveModeEnum = this.store.context.getters['GetSaveMode']; 

        const args: Dictionary<any> = {};
        args["Token"] = Vue.cookies.get('access_token') != null ? Vue.cookies.get('access_token') : "";
        args["ObjectDashboard"] = JSON.stringify(dashboard);
        args["SaveMode"] = saveMode;

        try{
            const response: AxiosResponse<any,any> = await axios.post(API_URL + "ObjectDashboard/Save", args);
            if (response && response.data) {
                /** Nous actualisons le nom du nœud plutôt que tout recharger. */
                const currentNode: BaseTreeNode = this.store.context.getters['GetDashNode'];
                const savedDashboard: ObjectDashboard = response.data;
                currentNode.DashboardRef = savedDashboard.Id;
                currentNode.Id = savedDashboard.Id.toString();
                currentNode.Name = savedDashboard.Name;
                this.store.context.commit('setObjectDash', savedDashboard);
                this.store.context.commit('setSaveMode', { saveMode: SaveModeEnum.UpdateObject });
                this.store.context.commit('setIsModified', false);
            }
        }
        catch(err){
            console.error(err);
            throw err;
        }
    }

    /**
     * Ajoute un widget sur le tableau de bord
     * @param widget widget à ajouter
     */
    public AddWidget(widget: ObjectWidget): void {
        console.debug("ObjDashAdminSelected - AddWidget() - widget : ", widget);

        this.store.context.commit('setAddWidget', widget);
    }

    /**
     * Retire un widget
     * @param widgetId Identifiant du widget à retirer
     */
    public RemoveWidget(widgetId: number): void {
        this.store.context.commit('setRemoveWidget', widgetId);
    }

    /**
     * Met à jour les paramètre d'un widget
     * @param widgetId Identifiant du widget à retirer
     * @param parameters paramètres
     * @param name nom
     */
    public UpdateWidgetParameters(widgetId: number, parameters: any, name: string | undefined): void {
        /** Vérifie si le tableau à supprimer est le tableau courant */
        const currentDashboard: ObjectDashboard = this.store.context.getters['GetObjectDash']; 
        const dashboardId = currentDashboard.Id;
        
        let datas = { Parameters: parameters };
		if (name)
			datas['Name'] = name;

		this.store.context.commit('updateWidget', { dashboardId, widgetId, name, datas: datas }) ;
    }

    /**
     * Met à jour le widget
     * @param widgetId Identifiant du widget à retirer
     * @param datas Données du widget
     */
    public UpdateWidget(widgetId: number, datas: any) {
        /** Vérifie si le tableau à supprimer est le tableau courant */
        const currentDashboard: ObjectDashboard = this.store.context.getters['GetObjectDash']; 
        const dashboardId = currentDashboard.Id;

		this.store.context.commit('updateWidget', { dashboardId, widgetId, datas }) ;
    }

//#region VIDE CAR : Inutile à cette étape 
    /** Inutile à cette étape */
    public async LoadTree(nodeIdToSelect: string): Promise<void> { }
//#endregion
}